import {
    ApartmentOutlined,
    FactoryOutlined,
    LocalShippingOutlined,
    LocationOnOutlined,
    StorefrontOutlined,
} from '@mui/icons-material';
import {FC, ReactNode, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {LocationType} from 'src/enums/locations';
import {LocationStatsStripType} from './LocationStatsStrip.types';

/** Every type the column can hold, in the order they read: homes, shops, warehouses, plants. */
const STRIP_TYPES: LocationType[] = [
    LocationType.RESIDENTIAL,
    LocationType.COMMERCIAL,
    LocationType.LOGISTICS,
    LocationType.INDUSTRIAL,
];

const TYPE_ICONS: Record<string, ReactNode> = {
    [LocationType.RESIDENTIAL]: <ApartmentOutlined/>,
    [LocationType.COMMERCIAL]: <StorefrontOutlined/>,
    [LocationType.LOGISTICS]: <LocalShippingOutlined/>,
    [LocationType.INDUSTRIAL]: <FactoryOutlined/>,
};

const TYPE_TONES: Record<string, StatsStripTone> = {
    [LocationType.RESIDENTIAL]: 'secondary',
    [LocationType.COMMERCIAL]: 'primary',
    [LocationType.LOGISTICS]: 'success',
    [LocationType.INDUSTRIAL]: 'warning',
};

export const LocationStatsStrip: FC<LocationStatsStripType> = ({
                                                                   data,
                                                                   activeType,
                                                                   onSelectType,
                                                                   activeGap,
                                                                   onSelectGap,
                                                               }) => {
    const intl = useIntl();

    const counts = useMemo(
        () => new Map((data?.types ?? []).map((point) => [point.type, point.locations])),
        [data]
    );

    return (
        <StatsStrip
            icon={<LocationOnOutlined/>}
            title="locations"
            subtitle={intl.formatMessage(
                {id: 'locationStatsSubtitle'},
                {
                    chargers: data?.chargers ?? 0,
                    parkingLots: data?.parkingLots ?? 0,
                    amperes: data?.availableAmperes ?? 0,
                }
            )}
            // The two gaps are the other axis: not a kind of site, but a site that is not finished.
            pills={[
                {
                    key: 'withoutChargers',
                    label: 'locationsWithoutChargers',
                    value: data?.withoutChargers,
                    tone: 'warning',
                    active: activeGap === 'withoutChargers',
                    onClick: onSelectGap ? () => onSelectGap('withoutChargers') : undefined,
                },
                {
                    key: 'withoutPrice',
                    label: 'locationsWithoutPrice',
                    value: data?.withoutPrice,
                    tone: 'danger',
                    active: activeGap === 'withoutPrice',
                    onClick: onSelectGap ? () => onSelectGap('withoutPrice') : undefined,
                },
            ]}
            tiles={STRIP_TYPES.map((type) => ({
                key: type,
                label: type,
                value: counts.get(type),
                icon: TYPE_ICONS[type],
                tone: TYPE_TONES[type],
                active: activeType === type,
                onClick: onSelectType ? () => onSelectType(type) : undefined,
            }))}
        />
    );
};
